import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import adminServices from '../../services/AdminServices';

function NurseRequestChart({ period = 'daily' }) {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await adminServices.nurseRequestCharts(`?period=${period}`);
                setChartData(res.data);
            } catch (err) {
                console.error('Nurse request chart error:', err);
                setError('Failed to load request metrics');
                setChartData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [period]);

    useEffect(() => {
        if (!chartRef.current || !Array.isArray(chartData) || chartData.length === 0 || loading) {
            return;
        }

        const ctx = chartRef.current.getContext('2d');
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const labels = chartData.map((item) => item.date);

        const colors = {
            pending: { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },    // amber-500
            matched: { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },     // blue-500
            accepted: { border: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },    // violet-500
            completed: { border: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },   // emerald-500
            cancelled: { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },    // red-500
        };

        chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Pending',
                        data: chartData.map((item) => item.pending),
                        borderColor: colors.pending.border,
                        backgroundColor: colors.pending.bg,
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Matched',
                        data: chartData.map((item) => item.matched),
                        borderColor: colors.matched.border,
                        backgroundColor: colors.matched.bg,
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Accepted',
                        data: chartData.map((item) => item.accepted),
                        borderColor: colors.accepted.border,
                        backgroundColor: colors.accepted.bg,
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Completed',
                        data: chartData.map((item) => item.completed),
                        borderColor: colors.completed.border,
                        backgroundColor: colors.completed.bg,
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Cancelled',
                        data: chartData.map((item) => item.cancelled),
                        borderColor: colors.cancelled.border,
                        backgroundColor: colors.cancelled.bg,
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y',
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: { color: '#1e293b' },
                    },
                    y: {
                        position: 'left',
                        title: { display: true, text: 'Requests', color: '#1e293b' },
                        grid: { color: 'rgba(0,0,0,0.05)' },
                        ticks: { color: '#1e293b' },
                        beginAtZero: true,
                        min: 0,
                    },
                },
                plugins: {
                    title: {
                        display: true,
                        text: period === 'daily'
                            ? 'Nurse Requests (Last 30 Days)'
                            : 'Nurse Requests (Last 12 Months)',
                        color: '#1e293b',
                        font: { size: 16, weight: 'bold' },
                        padding: { top: 10, bottom: 15 }
                    },
                    legend: {
                        labels: {
                            color: '#1e293b',
                            usePointStyle: true,
                            padding: 10,
                        },
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: '#1e293b',
                        titleColor: '#f1f5f9',
                        bodyColor: '#f1f5f9',
                        callbacks: {
                            label: function (context) {
                                let label = context.dataset.label || '';
                                if (label) label += ': ';
                                label += context.parsed.y;
                                return label;
                            },
                        },
                    },
                },
            },
        });
    }, [chartData, loading, period]);

    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    return (
        <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-border-light dark:border-border-dark">
            {loading ? (
                <div className="h-64 flex items-center justify-center text-gray-500">
                    Loading request metrics...
                </div>
            ) : error ? (
                <div className="h-64 flex items-center justify-center text-error">
                    {error}
                </div>
            ) : chartData && chartData.length > 0 ? (
                <div className="h-72">
                    <canvas ref={chartRef} />
                </div>
            ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                    No request data available
                </div>
            )}
        </div>
    );
}

export default NurseRequestChart;